import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Pagination,
  Select,
  Typography,
} from "@mui/material";
import {
  DataGrid,
  GridFooterContainer,
  GridToolbarContainer,
  useGridApiContext,
  useGridSelector,
  gridPageCountSelector,
  gridPaginationModelSelector,
  gridPaginationRowCountSelector,
} from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import customDataGridStyles from "./customDataGridStyles";

const readPath = (object, path) =>
  path.split(".").reduce((value, key) => value?.[key], object);

const GridToolbar = ({ fileName, onRefresh, columns, filterContent, showActions, customToolbar: CustomToolbar }) => {
  const apiRef = useGridApiContext();
  const [search, setSearch] = useState("");

  const updateSearch = (value) => {
    setSearch(value);
    apiRef.current.setQuickFilterValues(value ? [value] : []);
  };

  const exportExcel = () => {
    const ids = apiRef.current.getSortedRowIds();
    const records = ids.map((id) => apiRef.current.getRow(id));
    const exportColumns = columns.filter(
      (column) => column.enableExport !== false
    );
    const rows = records.map((record) =>
      Object.fromEntries(
        exportColumns.map((column) => [column.header, record[column.id] ?? ""])
      )
    );
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Records");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  };

  return (
    <>
      {CustomToolbar ? <CustomToolbar /> : showActions && (
        <GridToolbarContainer sx={{ justifyContent: "flex-end", gap: 1, px: 1.5, py: 1 }}>
          <Button size="small" variant="outlined" onClick={() => apiRef.current.showPreferences("filters")}>Filters</Button>
          <Button size="small" variant="outlined" onClick={() => apiRef.current.exportDataAsCsv({ fileName })}>CSV</Button>
          <Button size="small" variant="outlined" onClick={exportExcel}>Excel</Button>
          {onRefresh && <Button className="piers-refresh-button" onClick={onRefresh}>Refresh</Button>}
        </GridToolbarContainer>
      )}
      <div className="piers-grid-filters" onClickCapture={(event) => {
        const button = event.target.closest("button");
        if (button && /^clear(?: filters)?$/i.test(button.textContent.trim())) {
          setSearch("");
          apiRef.current.setFilterModel({ items: [], quickFilterValues: [] });
          apiRef.current.setPage(0);
        }
      }}>
        {filterContent || <div className="piers-grid-filters__default">
          <label className="piers-grid-filters__search">
            <span className="visually-hidden">Search records</span>
            <input value={search} onChange={(event) => updateSearch(event.target.value)} placeholder="Search records..." />
          </label>
          <button type="button" onClick={() => {
            setSearch("");
            apiRef.current.setFilterModel({ items: [], quickFilterValues: [] });
          }}>Clear</button>
        </div>}
      </div>
    </>
  );
};

const GridFooter = ({
  recordLabel = "inspection cycles",
  footerPagination,
}) => {
  const apiRef = useGridApiContext();
  const gridPaginationModel = useGridSelector(
    apiRef,
    gridPaginationModelSelector
  );
  const gridPageCount = useGridSelector(apiRef, gridPageCountSelector);
  const gridRowCount = useGridSelector(apiRef, gridPaginationRowCountSelector);
  const paginationModel = footerPagination?.model || gridPaginationModel;
  const rowCount = footerPagination?.rowCount ?? gridRowCount;
  const allowAll = footerPagination?.allowAll ?? true;
  const allRows = allowAll && paginationModel.pageSize === -1;
  const effectivePageSize = allRows ? Math.max(1, rowCount) : paginationModel.pageSize;
  const pageCount = footerPagination
    ? Math.max(1, Math.ceil(rowCount / effectivePageSize))
    : gridPageCount;
  const start = rowCount
    ? paginationModel.page * effectivePageSize + 1
    : 0;
  const end = Math.min(
    (paginationModel.page + 1) * effectivePageSize,
    rowCount
  );

  const updatePagination = (next) => {
    if (footerPagination?.onChange) footerPagination.onChange(next);
    else if (next.pageSize !== paginationModel.pageSize)
      apiRef.current.setPageSize(next.pageSize);
    else apiRef.current.setPage(next.page);
  };

  return (
    <GridFooterContainer
      sx={{
        display: "flex",
        justifyContent: "space-between",
        minHeight: 58,
        px: 2,
        borderTop: "1px solid #e3e8f0",
        color: "#5e6f86",
      }}
    >
      <Typography variant="body2">
        Showing {start}–{end} of {rowCount} {recordLabel}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: 13 }}
        >
          Rows per page
          <Select
            size="small"
            value={paginationModel.pageSize}
            onChange={(event) =>
              updatePagination({
                page: 0,
                pageSize: Number(event.target.value),
              })
            }
            sx={{
              minWidth: 72,
              fontSize: 13,
              "& .MuiSelect-select": { py: 0.6 },
            }}
          >
            {allowAll && <MenuItem value={-1}>All</MenuItem>}
            {[5, 10, 15, 20, 25, 50].map((size) => (
              <MenuItem key={size} value={size}>
                {size}
              </MenuItem>
            ))}
          </Select>
        </Box>
        {pageCount > 1 ? (
          <Pagination
            size="small"
            count={pageCount}
            page={paginationModel.page + 1}
            onChange={(_, page) =>
              updatePagination({ ...paginationModel, page: page - 1 })
            }
            shape="rounded"
            color="primary"
          />
        ) : (
          <Typography variant="body2">Page 1 of 1</Typography>
        )}
      </Box>
    </GridFooterContainer>
  );
};

const GridEmptyState = ({ state, filtered = false }) => {
  const apiRef = useGridApiContext();

  const reset = () => {
    apiRef.current.setFilterModel({ items: [], quickFilterValues: [] });
    state?.onReset?.();
  };
  if (!state && !filtered)
    return <Box sx={{ p: 4, textAlign: "center" }}>No rows</Box>;
  const noMatches = filtered || state?.filtered;
  return (
    <Box
      role={state?.error ? "alert" : "status"}
      sx={{
        height: "100%",
        minHeight: 220,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 1.5,
        p: 3,
        textAlign: "center",
        background: "#fff",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          background: "#edf4ff",
          color: "#0057ff",
          borderRadius: "50%",
          width: 52,
          height: 52,
          display: "grid",
          placeItems: "center",
          fontSize: 25,
        }}
      >
        <i
          className={`bx ${
            state?.error
              ? "bx-error-circle"
              : noMatches
              ? "bx-search"
              : state?.icon || "bx-folder-open"
          }`}
        />
      </Box>
      <Typography component="h3" sx={{ fontWeight: 700, fontSize: 18 }}>
        {state?.error
          ? "Unable to load records"
          : noMatches
          ? "No matching records"
          : state?.title || "No records yet"}
      </Typography>
      <Typography sx={{ maxWidth: 500, color: "#52698e", fontSize: 14 }}>
        {state?.error ||
          (noMatches
            ? "Try clearing your filters to see available records."
            : state?.description)}
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "center",
        }}
      >
        {state?.error ? (
          state?.onRetry && <Button onClick={state.onRetry}>Retry</Button>
        ) : noMatches ? (
          <Button onClick={reset}>Clear filters</Button>
        ) : (
          <>
            {state?.onAction && (
              <Button variant="contained" onClick={state.onAction}>
                {state.actionLabel}
              </Button>
            )}
            {state?.onSecondary && (
              <Button onClick={state.onSecondary}>
                {state.secondaryLabel}
              </Button>
            )}
          </>
        )}
      </Box>
      {!state?.error && !noMatches && state?.helpHref && (
        <a href={state.helpHref}>Learn more →</a>
      )}
    </Box>
  );
};

const CustomDataGrid = ({
  data = [],
  columns,
  onRefresh,
  emptyState,
  emptyMessage,
  fileName = "export",
  recordLabel = "inspection cycles",
  initialPageSize = 15,
  showToolbar = true,
  toolbar: ToolbarComponent,
  filterContent,
  gridSx = {},
  footerPagination,
  ...gridProps
}) => {
  const rows = useMemo(
    () =>
      data.map((record, index) => {
        const row = {
          id:
            record.gridRowId ??
            record.id ??
            record.inspectionCycleId ??
            `row-${index}`,
          __original: record,
        };
        columns.forEach((column) => {
          const id = column.id || column.accessorKey;
          row[id] = column.accessorFn
            ? column.accessorFn(record)
            : readPath(record, column.accessorKey || id);
        });
        return row;
      }),
    [data, columns]
  );

  const gridColumns = useMemo(
    () =>
      columns.map((column) => {
        const field = column.id || column.accessorKey;
        const sortable = column.enableSorting !== false;
        const base = {
          field,
          headerName: column.header,
          width: column.size || 150,
          minWidth: column.minSize || 90,
          flex: column.flex ?? 1,
          align: column.align,
          headerAlign: column.headerAlign,
          sortable,
          filterable: column.enableColumnFilter !== false,
          disableExport: column.enableExport === false,
        };

        if (column.getActions) {
          return {
            ...base,
            type: "actions",
            getActions: (params) =>
              column.getActions({ row: params.row.__original }),
          };
        }

        return {
          ...base,
          renderCell: column.cell
            ? (params) =>
                column.cell({
                  row: { original: params.row.__original },
                  getValue: () => params.value,
                })
            : undefined,
        };
      }),
    [columns]
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "auto",
      }}
    >
      {emptyState?.error && data.length > 0 && (
        <Box
          role="alert"
          sx={{ p: 2, background: "#fff3f3", color: "#932b3a" }}
        >
          {emptyState.error} Previously loaded records are shown.{" "}
          {emptyState.onRetry && (
            <Button onClick={emptyState.onRetry}>Retry</Button>
          )}
        </Box>
      )}
      {/* Let the containing page or dialog scroll vertically for every grid. */}
      <DataGrid
        {...gridProps}
        showToolbar
        autoHeight
        rowHeight={Math.max(gridProps.rowHeight || 54, 54)}
        columnHeaderHeight={Math.max(gridProps.columnHeaderHeight || 56, 50)}
        rows={rows}
        columns={gridColumns}
        disableRowSelectionOnClick
        pageSizeOptions={[5, 10, 15, 20, 25, 50, { value: -1, label: "All" }]}
        initialState={{
          pagination: {
            paginationModel: { pageSize: initialPageSize, page: 0 },
          },
        }}
        slots={{
          toolbar: GridToolbar,
          footer: GridFooter,
          noRowsOverlay: GridEmptyState,
          noResultsOverlay: GridEmptyState,
          ...gridProps.slots,
        }}
        slotProps={{
          toolbar: { fileName, onRefresh, columns, filterContent, showActions: showToolbar, customToolbar: ToolbarComponent },
          footer: { recordLabel, footerPagination },
          noRowsOverlay: { state: emptyState },
          noResultsOverlay: { state: emptyState, filtered: true },
          ...gridProps.slotProps,
          columnMenu: {
            ...gridProps.slotProps?.columnMenu,
            sx: [
              {
                "& .MuiMenuItem-root, & .MuiTypography-root": {
                  fontSize: 13,
                },
                "& .MuiMenuItem-root .MuiSvgIcon-root": { fontSize: 18 },
              },
              ...(Array.isArray(gridProps.slotProps?.columnMenu?.sx)
                ? gridProps.slotProps.columnMenu.sx
                : [gridProps.slotProps?.columnMenu?.sx || {}]),
            ],
          },
        }}
        sx={[
          gridSx,
          customDataGridStyles,
          emptyState ? { "--DataGrid-overlayHeight": "280px" } : {},
        ]}
      />
    </Box>
  );
};

export default CustomDataGrid;
